/* assets/lab-bottom-strip.js — the phone bottom strip (DIRECTION.md §4 "Phone bottom
   strip", §6 interaction 7). Defines <lab-bottom-strip>:

   - Carrello · total: opens the cart sheet through cart-drawer.open(cell) when a sheet
     script is loaded, otherwise goes to the cart page; the total follows every
     cart-update pub/sub payload (cartData.total_price, or /cart.js when the payload is
     an added line) and changes through a --d-2 crossfade in document.startViewTransition;
   - the product mirror: binds its submit button to the kept product form (form attribute),
     names the form's buy button's view timeline --lab-buy (the strip's CSS shows the
     mirror once that button scrolls out at the top), mirrors the selected variant's price
     and availability from the variant-change payload and the buy button's loading class;
   - the receipt: for six seconds after an add from a product form the cells read
     Aggiunto · total and Checkout → (silent: cart-drawer.renderContents() already announces
     the added product at every width).

   Registered behind customElements.get; listeners removed with an AbortController and the
   pub/sub subscriptions released in disconnectedCallback. */

if (!customElements.get('lab-bottom-strip')) {
  const ADDED_MS = 6000;
  const REDUCED = '(prefers-reduced-motion: reduce)';

  const formatMoney = (cents, format) => {
    const amount = Number(cents) / 100;
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(pattern);
    const key = match ? match[1] : 'amount';
    const fixed = (decimals, thousands, decimal) => {
      const [whole, fraction] = amount.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    let value;
    switch (key) {
      case 'amount_no_decimals':
        value = fixed(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = fixed(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = fixed(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = fixed(2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = fixed(0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = fixed(2, ' ', ',');
        break;
      default:
        value = fixed(2, ',', '.');
    }
    return format.replace(pattern, value);
  };

  class LabBottomStrip extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.totalGen = 0;
      this.moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
      this.totalEl = this.querySelector('[data-strip-total]');
      this.addedTotalEl = this.querySelector('[data-strip-added-total]');
      this.priceEl = this.querySelector('[data-strip-price]');
      this.buyButton = this.querySelector('[data-strip-buy]');
      this.notifyButton = this.querySelector('[data-strip-notify]');
      this.cartButton = this.querySelector('[data-strip-cart]');
      this.checkoutLink = this.querySelector('[data-strip-checkout]');

      this.cartButton?.addEventListener('click', () => this.openCart(), { signal });
      this.notifyButton?.addEventListener('click', () => this.scrollToNotify(), { signal });

      if (this.dataset.product) this.wireProduct(signal);
      // the strip lives in the layout, so an editor reload of main-product leaves it bound
      // to the removed form and buy button; rebind (wireProduct drops the old observers)
      document.addEventListener('shopify:section:load', () => {
        if (this.dataset.product) this.wireProduct(signal);
      }, { signal });

      // pubsub.js and constants.js are classic scripts: `subscribe` is a global function,
      // PUB_SUB_EVENTS a top-level const (a global binding, not a window property)
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
      }
    }

    disconnectedCallback() {
      this.controller.abort();
      this.unsubscribeCart?.();
      this.unsubscribeVariant?.();
      this.observer?.disconnect();
      this.notifyWatch?.disconnect();
      clearTimeout(this.addedTimer);
      this.addedBlur?.abort();
    }

    /* ---------- cart ---------- */

    openCart() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(this.cartButton);
        return;
      }
      window.location.assign(this.dataset.cartUrl || '/cart');
    }

    onCartUpdate(event) {
      const data = event?.cartData;
      // last request wins: an add's /cart.js reply can land after a newer update's total
      const token = ++this.totalGen;
      const total = typeof data?.total_price === 'number' ? Promise.resolve(data.total_price) : this.fetchTotal();
      total
        .then((cents) => {
          if (token !== this.totalGen) return;
          this.setTotal(cents);
          if (event?.source === 'product-form') this.showAdded(cents);
        })
        .catch((error) => console.error(error));
    }

    fetchTotal() {
      return fetch(`${this.dataset.cartUrl || '/cart'}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
          return response.json();
        })
        .then((cart) => cart.total_price);
    }

    setTotal(cents) {
      const text = formatMoney(cents, this.moneyFormat);
      const apply = () => {
        if (this.totalEl) this.totalEl.textContent = text;
        if (this.addedTotalEl) this.addedTotalEl.textContent = text;
      };
      this.crossfade(apply);
    }

    crossfade(apply) {
      const reduced = window.matchMedia(REDUCED).matches;
      const designMode = window.Shopify && window.Shopify.designMode;
      if (typeof document.startViewTransition !== 'function' || reduced || designMode) {
        apply();
        return;
      }
      document.documentElement.classList.add('lab-strip-vt');
      const transition = document.startViewTransition(apply);
      transition.finished.finally(() => document.documentElement.classList.remove('lab-strip-vt'));
    }

    showAdded(cents) {
      clearTimeout(this.addedTimer);
      // a newer receipt cancels a revert still waiting for the Checkout link to lose focus
      this.addedBlur?.abort();
      if (this.addedTotalEl) this.addedTotalEl.textContent = formatMoney(cents, this.moneyFormat);
      const layer = this.querySelector('.lab-strip__added');
      this.dataset.state = 'added';
      layer?.removeAttribute('aria-hidden');
      this.checkoutLink?.removeAttribute('tabindex');
      const revert = () => {
        this.dataset.state = 'default';
        layer?.setAttribute('aria-hidden', 'true');
        this.checkoutLink?.setAttribute('tabindex', '-1');
      };
      this.addedTimer = setTimeout(() => {
        // hiding the layer under keyboard focus would drop focus to <body>: wait for blur
        if (this.checkoutLink && document.activeElement === this.checkoutLink) {
          this.addedBlur = new AbortController();
          // a window blur (tab or app switch) also fires blur but leaves the link focused: re-arm
          const onBlur = () => {
            if (document.activeElement === this.checkoutLink) {
              this.checkoutLink.addEventListener('blur', onBlur, { once: true, signal: this.addedBlur.signal });
              return;
            }
            revert();
          };
          this.checkoutLink.addEventListener('blur', onBlur, { once: true, signal: this.addedBlur.signal });
          return;
        }
        revert();
      }, ADDED_MS);
    }

    /* ---------- product mirror ---------- */

    wireProduct(signal) {
      if (!this.buyButton) return;
      this.observer?.disconnect();
      this.notifyWatch?.disconnect();
      delete this.dataset.notifyMode;
      const form = document.querySelector('product-form form[action*="/cart/add"]');
      this.form = form;
      const submit = form ? form.querySelector('[type="submit"]') : null;

      if (form) {
        this.buyButton.setAttribute('form', form.id);
        form.addEventListener('submit', () => this.buyButton.classList.add('loading'), { signal });
        if (submit) {
          this.observer = new MutationObserver(() => this.mirrorButtonState(submit));
          this.observer.observe(submit, { attributes: true, attributeFilter: ['class', 'aria-disabled', 'disabled'] });
          this.mirrorButtonState(submit);
        }
      } else {
        // a sold-out variant has no add-to-cart form at all: the buy column holds the
        // back-in-stock field instead, so the mirror offers Avvisami and nothing else.
        this.buyButton.hidden = true;
        if (this.notifyButton) this.notifyButton.hidden = false;
      }

      // The subject whose exit drives the swap (animation-timeline: --lab-buy) is the buy
      // button when the variant can be bought, and the back-in-stock field when it cannot;
      // without a subject the mirror would never appear on a sold-out product.
      const subject = submit || document.querySelector('[data-back-in-stock], .back-in-stock, back-in-stock-form');
      if (!subject) return;
      if (!submit) {
        this.watchNotify(subject);
        return;
      }
      subject.style.setProperty('view-timeline-name', '--lab-buy');
      subject.style.setProperty('view-timeline-inset', 'var(--chrome-h) var(--strip-h)');
      this.dataset.mirror = '';
    }

    /* Sold out, the mirror (ESAURITO — AVVISAMI →) is shown whenever the back-in-stock field
       is off screen, above it as well as below. The scroll timeline only knows "has left at
       the top", and on a phone the field sits some 2,000 px down the page: a visitor had to
       scroll past it before the strip offered the one thing the page can do. */
    watchNotify(subject) {
      this.dataset.notifyMode = '';
      this.notifyWatch = new IntersectionObserver(([entry]) => {
        this.toggleAttribute('data-notify-near', entry.isIntersecting);
      });
      this.notifyWatch.observe(subject);
    }

    mirrorButtonState(submit) {
      const loading = submit.classList.contains('loading');
      this.buyButton.classList.toggle('loading', loading);
      this.buyButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      const disabled = submit.getAttribute('aria-disabled') === 'true' || submit.disabled;
      this.buyButton.setAttribute('aria-disabled', String(disabled));
    }

    onVariantChange(event) {
      const variant = event?.data?.variant || event?.variant;
      if (!this.buyButton || !this.notifyButton) return;
      if (!variant) {
        this.buyButton.hidden = true;
        this.notifyButton.hidden = false;
        return;
      }
      if (this.priceEl && typeof variant.price === 'number') {
        this.priceEl.textContent = formatMoney(variant.price, this.moneyFormat);
      }
      // product-info re-renders the buy column on every option change: a variant that can
      // be bought brings back the add-to-cart form the sold-out one did not have, so the
      // mirror binds to it now (and to nothing when the form is still absent).
      if (!this.form || !this.form.isConnected) this.wireProduct(this.controller.signal);
      const buyable = Boolean(variant.available && this.form);
      this.buyButton.hidden = !buyable;
      this.notifyButton.hidden = buyable;
    }

    scrollToNotify() {
      const target =
        document.querySelector('[data-back-in-stock], .back-in-stock, back-in-stock-form, #BackInStock') || this.form;
      if (!target) return;
      target.scrollIntoView({ block: 'center', behavior: window.matchMedia(REDUCED).matches ? 'auto' : 'smooth' });
      const field = target.querySelector('input:not([type="hidden"]), button');
      field?.focus({ preventScroll: true });
    }
  }

  customElements.define('lab-bottom-strip', LabBottomStrip);
}
